import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import OrderCard from '../../components/OrderCard';
import PageHeader from '../../components/ui/PageHeader';
import StatsCard from '../../components/ui/StatsCard';
import Button from '../../components/ui/Button';
import Pagination from '../../components/ui/Pagination';
import useOrderFilters from '../../hooks/useOrderFilters';
import OrderFilterPanel from '../../components/OrderFilterPanel';

const PAGE_SIZE = 8;

export default function CompanyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const {
    filteredOrders,
    search, setSearch,
    statusFilter, setStatusFilter,
    datePreset, setDatePreset,
    sortDir, setSortDir,
    activeFilterCount,
    clearFilters,
  } = useOrderFilters(orders);

  const handleClearFilters = () => { clearFilters(); setIsFilterOpen(false); };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_URL}/api/orders/incoming`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setOrders(res.data.orders ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [filteredOrders]);

  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const totalPages = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const pagedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="bg-sand-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        <PageHeader
          title="Incoming Requests"
          subtitle={`Service requests from your customers, ${user?.first_name}`}
        />

        <div className="grid grid-cols-2 gap-4 mb-8">
          <StatsCard label="Pending Requests" value={pendingOrders} />
          <StatsCard label="Total Requests" value={orders.length} />
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <div role="status" aria-label="Loading" className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <p className="font-heading text-lg font-semibold text-ink-900">No incoming requests yet</p>
            <p className="text-sm text-ink-400 mb-2">Create a listing so customers can find and contact you</p>
            <Link to="/listings/create"><Button variant="secondary" size="sm">Create Listing</Button></Link>
          </div>
        ) : (
          <>
            <div className="relative mb-3">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                aria-label="Search orders"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by customer, order ID, or listing…"
                className="w-full pl-9 pr-4 py-2.5 border border-sand-200 rounded-lg text-sm text-ink-900 placeholder-ink-300 bg-white focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-transparent transition"
              />
            </div>

            <OrderFilterPanel
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(v => !v)}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              datePreset={datePreset}
              setDatePreset={setDatePreset}
              sortDir={sortDir}
              setSortDir={setSortDir}
              activeFilterCount={activeFilterCount}
              clearFilters={handleClearFilters}
            />

            {filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center py-12 gap-2">
                <p className="font-heading text-base font-semibold text-ink-900">No orders match your filters</p>
                <button type="button" onClick={handleClearFilters} className="text-sm text-terra-400 hover:text-terra-500 font-medium transition-colors">
                  Clear filters
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-3">
                  {pagedOrders.map(o => <OrderCard key={o.id} order={o} viewAs="provider" />)}
                </div>
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
